import { useEffect, useState } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useHistory, Redirect } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import { analytics } from '@eti/utils';
import { seatMap } from '@eti/products';
import { useProperty } from '@eti/providers';
import type { GetSelectedTripOnTravelerDetailsPageQuery } from '@eti/schema-types';
import { routes } from '../../../constants/routesAndApisConstants';
import TravelerDetailsNoTripErrorDialog from '../components/TravelerDetailsNoTripErrorDialog';
import TravelerDetailsNoTripNoCartErrorDialog from '../components/TravelerDetailsNoTripNoCartErrorDialog';
import TravelerDetailsPendingPaymentDialog from '../components/TravelerDetailsPendingPaymentDialog';
import { UPDATE_TRIP_BEFORE_RESERVATION } from '../../../utils/mutations';
import NoSelectedTripErrorDialog from '../../../common/error-modals/NoSelectedTripErrorDialog';
import TravelerDetailsSkeleton from '../components/TravelerDetailsSkeleton';
import { ADD_TO_CART, CANCEL_MAKE_ORDER_MUTATION } from '../graphql/mutations';
import { TRAVELER_DETAILS_QUERY } from '../graphql/queries';
import TravelerDetailsContainer from './TravelerDetailsContainer';

const hasNoSelectedTrip = (data: GetSelectedTripOnTravelerDetailsPageQuery) =>
  isEmpty(data?.selectedTrip ?? {});

const hasCart = (data: GetSelectedTripOnTravelerDetailsPageQuery) => Boolean(data?.cart?.id);

const TravelerDetailsGraphqlContainer = ({
  headerAndInfoHeight,
}: {
  headerAndInfoHeight: number;
}) => {
  const history = useHistory();
  const { p } = useProperty();
  const isDynamicBookingFlowEnabled = p('TravelerDetails.DynamicFlow.Enabled');
  const [shouldShowTripIsNotAvailableModal, setShowTripIsNotAvailableModal] = useState(false);
  const [selectedTripError, setSelectedTripError] = useState<string | null>(null);
  const [
    getTravelerDetails,
    { data, loading: isQueryLoading, called: isQueryCalled, error: mutationError, refetch },
  ] = useLazyQuery(TRAVELER_DETAILS_QUERY, {
    variables: { excludedExtraProductIds: [String(seatMap.id)] },
    fetchPolicy: 'no-cache',
    onError: () => {
      if (mutationError) {
        const errorCode = mutationError.graphQLErrors[0]?.message;
        setSelectedTripError(errorCode);
      }
    },
  });

  analytics.useLogMouseFlowTagEffect('DBF_v1', isDynamicBookingFlowEnabled);

  const isQueryDone = isQueryCalled && !isQueryLoading;

  const [updateTrip, { loading: updateTripLoading, called: isUpdateTripCalled }] = useMutation(
    UPDATE_TRIP_BEFORE_RESERVATION,
    {
      onError: () => {
        setShowTripIsNotAvailableModal(true);
      },
    },
  );
  const isOrderStatusPageEnabled = p('OrderStatusPage.Enabled');

  const isUpdateTripDone = isUpdateTripCalled && !updateTripLoading;

  useEffect(() => {
    if (isUpdateTripDone) {
      getTravelerDetails();
    }
  }, [isUpdateTripDone, getTravelerDetails]);

  const [addToCart, { loading: isLoading }] = useMutation(ADD_TO_CART, {
    onCompleted: ({ addToCart: addToCartResponse }) => {
      if (addToCartResponse.success) {
        history.push(routes.PAYMENT);
      } else {
        setShowTripIsNotAvailableModal(true);
      }
    },
    onError: () => {
      setShowTripIsNotAvailableModal(true);
    },
  });

  const [cancelMakeOrder] = useMutation(CANCEL_MAKE_ORDER_MUTATION, {
    onCompleted: () => {
      setSelectedTripError(null);
      if (refetch) {
        refetch();
      }
    },
  });

  useEffect(() => {
    if (!isUpdateTripCalled) {
      updateTrip();
    }
  }, [isUpdateTripCalled, updateTrip]);

  if (selectedTripError === 'PENDING_PAYMENT') {
    return <TravelerDetailsPendingPaymentDialog cancelMakeOrder={cancelMakeOrder} />;
  }

  if (
    selectedTripError === 'PENDING_PROVIDER_BOOKING' ||
    selectedTripError === 'PROCESSING_ORDER'
  ) {
    const processingOrderRoute = isOrderStatusPageEnabled
      ? routes.ORDER_STATUS
      : routes.PROCESSING_ORDER;

    <Redirect to={processingOrderRoute} />;
  }

  if (shouldShowTripIsNotAvailableModal) {
    return <NoSelectedTripErrorDialog />;
  }

  if (!isUpdateTripDone || !isQueryDone || !data) {
    return <TravelerDetailsSkeleton />;
  }

  if (hasNoSelectedTrip(data)) {
    return hasCart(data) ? (
      <TravelerDetailsNoTripErrorDialog
        isOpen
        onPrimaryAction={() => history.push(routes.PAYMENT)}
        onSecondaryAction={() => history.push(routes.START)}
      />
    ) : (
      <TravelerDetailsNoTripNoCartErrorDialog
        isOpen
        onPrimaryAction={() => history.push(routes.START)}
      />
    );
  }

  return (
    <TravelerDetailsContainer
      addToCart={addToCart}
      data={data}
      headerAndInfoHeight={headerAndInfoHeight}
      isLoading={isLoading}
    />
  );
};

export default TravelerDetailsGraphqlContainer;
